import React, { useCallback, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { handleSetPlugAndPlayInfos, wizardState } from "./botSlice";
import { ConnectForm } from "./BotWizard";
import InfoMessageAlert from "./InfoMessageAlert";
import { AIKittyType } from "./types";

const PlugAndPlayDomainLinks = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    clearErrors,
    setError,
    setValue,
    formState: { errors }
  } = useFormContext();

  const wizard = useAppSelector(wizardState);

  const plugAndPlayState = (wizard.bot_config as AIKittyType).plug_and_play;

  const domainIsUpdated: boolean =
    wizard.bot_ui_info.wizard?.step2.extraData?.isNewRequest ?? false;

  const [sitemaps, setSitemaps] = useState<string[]>([]);
  const [selectedSiteMaps, setSelectedSiteMaps] = useState<string[]>(
    plugAndPlayState?.links ?? []
  );

  const handleAddOrRemoveLink = useCallback(
    (sitemap: string, isChecked: boolean) => {
      clearErrors("links");

      setSelectedSiteMaps((prevSelected) => {
        const newSelected = new Set(prevSelected);

        if (isChecked) {
          if (newSelected.size < 20) {
            newSelected.add(sitemap);
          } else {
            setError("links", {
              type: "custom",
              message: t("Wizard/PlugAndPlayDomainLinks/key4")
            });
            return prevSelected;
          }
        } else {
          newSelected.delete(sitemap);
        }

        return Array.from(newSelected);
      });
    },
    [clearErrors, setError, t]
  );

  useEffect(() => {
    setValue("links", selectedSiteMaps);

    dispatch(handleSetPlugAndPlayInfos({ links: selectedSiteMaps }));
  }, [selectedSiteMaps]);

  useEffect(() => {
    if (plugAndPlayState?.address) {
      if (!domainIsUpdated)
        setSitemaps(
          wizard.bot_ui_info.wizard?.step2.extraData?.links ?? selectedSiteMaps
        );
      else {
        setValue("links", []);

        setSelectedSiteMaps([]);

        dispatch(handleSetPlugAndPlayInfos({ links: [] }));

        setSitemaps(wizard.bot_ui_info.wizard?.step2.extraData?.links ?? []);
      }
    }
  }, [plugAndPlayState?.address]);

  return (
    <ConnectForm>
      {() => (
        <div className="plug-and-play tw-h-full tw-py-4 md:tw-px-4">
          <div className="tw-w-full tw-h-full tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
            <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
              <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
                <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                  <div className="tw-self-stretch tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                    2. {t("Wizard/PlugAndPlayDomainLinks/key1")}
                  </div>
                  <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("Wizard/PlugAndPlayDomainLinks/key2")}
                  </div>
                </div>
              </div>
            </div>

            <InfoMessageAlert
              texts={[
                t("Wizard/PlugAndPlayAlert/key1"),
                t("Wizard/PlugAndPlayAlert/key2")
              ]}
            />

            <div className="tw-w-full tw-h-[calc(100%-135px)] tw-p-4 tw-bg-white tw-rounded-xl tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
              {(domainIsUpdated && !!errors.links) ||
                (!!errors.links && (
                  <div className="tw-pl-2 tw-pr-3 tw-py-1 tw-bg-orange-50 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-1.5 tw-flex">
                    <div className="tw-text-amber-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("Wizard/PlugAndPlayDomainLinks/er-01")}
                    </div>
                  </div>
                ))}

              <div className="tw-self-stretch tw-overflow-auto tw-px-2 tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex md:pb-0 pb-4">
                {sitemaps?.map((sitemap, index) => (
                  <div
                    key={index}
                    className="tw-self-stretch tw-py-0.5 tw-justify-start tw-items-center tw-gap-2 tw-inline-flex"
                  >
                    <Form.Check
                      type={"checkbox"}
                      label={sitemap}
                      className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                      onChange={(e) =>
                        handleAddOrRemoveLink(sitemap, e.target.checked)
                      }
                      checked={selectedSiteMaps.indexOf(sitemap) > -1}
                      id={`sitemap-${index}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </ConnectForm>
  );
};

export default PlugAndPlayDomainLinks;
